/* ZOO-ZI — пренесено от ~/zoo-zi-mockup/assets/app.js */
/* ═══════════════════════════════════════════════════════════════════════════
   mockup-app.js — the shared script for a static review mockup.
   One IIFE, zero dependencies, ~150 lines. Drop in as assets/app.js.

   Why this file exists: the reveal-on-scroll failsafe below is the single bug
   that ends a client review instantly — one JS exception and the reviewer opens
   a blank page. Both of the mockups this skill's protocol is derived from
   solve it the same way. Do not re-derive it; start here.
   See references/mockup-protocol.md §3 rule 9.

   CSS CONTRACT (put this in the stylesheet, exactly this way round):
     .rv        { opacity: 1; }                       ← default is VISIBLE
     .js .rv    { opacity: 0; transform: translateY(14px);
                  transition: opacity var(--dur-4) var(--ease),
                              transform var(--dur-4) var(--ease); }
     .js .rv.in { opacity: 1; transform: none; }
     @media (prefers-reduced-motion: reduce) {
       .js .rv  { opacity: 1 !important; transform: none !important; }
     }
   ═══════════════════════════════════════════════════════════════════════════ */
(() => {
  'use strict';
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* 1 ── THE FAILSAFE. Only now may CSS hide anything. If this script throws
         before this line, or never loads at all, the page renders complete. */
  if (!reduced) root.classList.add('js');

  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const NEAR = 0.92;                       // viewport fraction counted as "on screen"

  /* 2 ── Reveal on scroll, with a manual sweep.
         IntersectionObserver silently skips elements when the page jumps
         (anchor links, restored scroll, bfcache), so the observer alone is not
         enough — the sweep is the net, not a nicety. */
  const reveal = el => el.classList.add('in');
  const sweep = () => {
    for (const el of $$('.rv:not(.in)')) {
      if (el.getBoundingClientRect().top < innerHeight * NEAR) reveal(el);
    }
  };

  if (reduced) {
    $$('.rv').forEach(reveal);
  } else if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, obs) => {
      for (const e of entries) if (e.isIntersecting) { reveal(e.target); obs.unobserve(e.target); }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    $$('.rv').forEach(el => {
      // never hide what is already on screen at load — it would flash
      if (el.getBoundingClientRect().top < innerHeight * NEAR) reveal(el);
      else io.observe(el);
    });
  } else {
    $$('.rv').forEach(reveal);
  }

  let t = 0;
  const throttled = () => {
    const now = Date.now();
    if (now - t < 400) return;
    t = now; sweep();
  };
  addEventListener('scroll', throttled, { passive: true });
  addEventListener('resize', throttled, { passive: true });
  addEventListener('hashchange', sweep);
  addEventListener('pageshow', sweep);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) sweep(); });
  setTimeout(sweep, 2000);                 // emergency nets: a section must never
  setTimeout(sweep, 5000);                 // stay invisible forever

  /* 3 ── Header: stuck state + optional theme inversion over a dark hero. */
  const hdr = $('[data-header]');
  if (hdr) {
    const dark = $$('[data-dark-zone]');
    const onScroll = () => {
      hdr.classList.toggle('is-stuck', scrollY > 8);
      if (!dark.length) return;
      const probe = hdr.getBoundingClientRect().height * 0.6;
      hdr.classList.toggle('is-over-dark', dark.some(z => {
        const r = z.getBoundingClientRect();
        return r.top <= probe && r.bottom >= probe;
      }));
    };
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* 4 ── Sticky add-to-cart bar: appears once the real buy box has scrolled by,
         hides again over the footer. Measured precedent: Obvi's 77px PDP bar. */
  const bar = $('[data-sticky-atc]'), anchor = $('[data-buybox]'), foot = $('footer');
  if (bar && anchor) {
    const sync = () => {
      const past = anchor.getBoundingClientRect().bottom < 0;
      const inFooter = foot && foot.getBoundingClientRect().top < innerHeight - 40;
      bar.classList.toggle('is-on', past && !inFooter);
    };
    addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* 5 ── Count-up for stats. Eased, never linear — linear reads like a spinner.
         Markup: <span data-count="115000" data-suffix="+">0</span> */
  const easeOut = p => 1 - Math.pow(1 - p, 3);
  const countUp = el => {
    const to = parseFloat(el.dataset.count) || 0, dur = 1200, suffix = el.dataset.suffix || '';
    if (reduced) { el.textContent = to.toLocaleString() + suffix; return; }
    const t0 = performance.now();
    const step = now => {
      const p = Math.min((now - t0) / dur, 1);
      el.textContent = Math.round(to * easeOut(p)).toLocaleString() + suffix;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  if ('IntersectionObserver' in window) {
    const co = new IntersectionObserver((es, o) => {
      for (const e of es) if (e.isIntersecting) { countUp(e.target); o.unobserve(e.target); }
    }, { threshold: 0.4 });
    $$('[data-count]').forEach(el => co.observe(el));
  } else $$('[data-count]').forEach(countUp);

  /* 6 ── Tabs. Markup: [data-tabs] > [data-tab="id"] buttons + [data-panel="id"]. */
  $$('[data-tabs]').forEach(group => {
    const tabs = $$('[data-tab]', group);
    const show = id => {
      tabs.forEach(t => {
        const on = t.dataset.tab === id;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      $$('[data-panel]', group).forEach(p => { p.hidden = p.dataset.panel !== id; });
    };
    tabs.forEach(t => t.addEventListener('click', () => show(t.dataset.tab)));
    if (tabs[0]) show(tabs[0].dataset.tab);
  });

  /* 7 ── Drawer / mobile menu, with focus return and Esc. */
  $$('[data-open]').forEach(btn => btn.addEventListener('click', () => {
    const panel = $('#' + btn.dataset.open);
    if (!panel) return;
    panel.classList.add('is-on');
    root.style.overflow = 'hidden';
    panel.dataset.returnTo = btn.id || '';
    (panel.querySelector('[data-close], a, button') || panel).focus?.();
  }));
  const closeAll = () => {
    $$('.is-on[data-panel-root]').forEach(p => {
      p.classList.remove('is-on');
      const back = p.dataset.returnTo && document.getElementById(p.dataset.returnTo);
      back?.focus?.();
    });
    root.style.overflow = '';
  };
  $$('[data-close]').forEach(b => b.addEventListener('click', closeAll));
  addEventListener('keydown', e => { if (e.key === 'Escape') closeAll(); });

  /* 8 ── Scroll-spy for an in-page section nav. */
  const spy = $('[data-spy]');
  if (spy && 'IntersectionObserver' in window) {
    const links = $$('a[href^="#"]', spy);
    const so = new IntersectionObserver(es => {
      for (const e of es) if (e.isIntersecting) {
        links.forEach(l => l.classList.toggle('is-on', l.getAttribute('href') === '#' + e.target.id));
      }
    }, { rootMargin: '-42% 0px -52% 0px' });
    links.forEach(l => { const s = document.getElementById(l.getAttribute('href').slice(1)); if (s) so.observe(s); });
  }
})();

/* ═══════════════════════════════════════════════════════════════════════════
   ZOO-ZI additions — megamenu, transparent-header solidify, size ladder.
   Namespaced under zz- so the whole layer lifts into a Liquid theme intact.
   ═══════════════════════════════════════════════════════════════════════════ */
(() => {
  'use strict';
  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];


  /* 2 ── Megamenu. Hover on pointer devices, click everywhere, Escape closes,
         focus returns to the trigger. Closes on focus leaving the panel. */
  const closeAll = except => $$('[data-mega]').forEach(p => {
    if (p === except) return;
    p.classList.remove('is-open');
    const t = $(`[aria-controls="${p.id}"]`);
    if (t) t.setAttribute('aria-expanded', 'false');
  });

  $$('[data-mega-trigger]').forEach(trigger => {
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    const open = () => { closeAll(panel); panel.classList.add('is-open'); trigger.setAttribute('aria-expanded','true'); };
    const shut = () => { panel.classList.remove('is-open'); trigger.setAttribute('aria-expanded','false'); };

    trigger.addEventListener('click', e => {
      e.preventDefault();
      panel.classList.contains('is-open') ? shut() : open();
    });
    if (matchMedia('(hover:hover)').matches) {
      const zone = trigger.closest('.hdr') || document;
      trigger.addEventListener('mouseenter', open);
      zone.addEventListener('mouseleave', shut);
    }
    trigger.addEventListener('keydown', e => { if (e.key === 'Escape') { shut(); trigger.focus(); } });
    panel.addEventListener('keydown',   e => { if (e.key === 'Escape') { shut(); trigger.focus(); } });
  });
  addEventListener('click', e => { if (!e.target.closest('.hdr')) closeAll(null); });

  /* 3 ── Size ladder (the objection-killer). Picking a weight answers which
         toy size fits, in the buyer's own unit — kilos, not S/M/L. */
  const out = $('[data-ladder-out]');
  $$('[data-rung]').forEach(rung => {
    rung.addEventListener('click', () => {
      $$('[data-rung]').forEach(r => r.setAttribute('aria-pressed', 'false'));
      rung.setAttribute('aria-pressed', 'true');
      if (!out) return;
      out.querySelector('b').textContent = rung.dataset.size;
      out.querySelector('p').textContent = rung.dataset.advice;
    });
  });
})();

/* ═══════════════════════════════════════════════════════════════════════════
   PDP + quiz interaction. Same namespace rules: nothing here touches globals.
   ═══════════════════════════════════════════════════════════════════════════ */
(() => {
  'use strict';
  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];

  /* ── Variant picker. The sticky bar mirrors the choice so the buyer never
        loses track of what is actually in the button. */
  const satcVariant = $('[data-satc-variant]');
  $$('[data-variant]').forEach(btn => btn.addEventListener('click', () => {
    $$('[data-variant]').forEach(b => { b.classList.remove('is-on'); b.setAttribute('aria-pressed','false'); });
    btn.classList.add('is-on'); btn.setAttribute('aria-pressed','true');
    if (satcVariant) satcVariant.textContent = 'Размер ' + btn.textContent.trim();
  }));

  /* ── Quantity stepper, clamped at 1. */
  const qty = $('#qty');
  $$('[data-qty]').forEach(b => b.addEventListener('click', () => {
    if (!qty) return;
    qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.qty, 10));
  }));

  /* ── Липкавата капсула. Ref ~/maxilin/site syncDock: влиза, когато истинският
        бутон е излязъл НАГОРЕ от кадъра, и се прибира, когато футърът влезе —
        иначе виси върху футъра и повтаря призив, който вече не е актуален. */
  const satc = $('#satc'), buy = $('.pdp__qty');
  if (satc && buy) {
    const syncDock = () => {
      /* MaXilin гаси капсулата, щом футърът покаже ръб. Там страницата е
         11 620px и това значи „почти в края"; тази е 3 400px и същият праг
         я гасеше още на средата. По искане на клиента (19.09) капсулата
         НЕ се крие във футъра — стои, докато страницата свърши. */
      satc.classList.toggle('is-on', buy.getBoundingClientRect().bottom < 0);
    };
    syncDock();
    addEventListener('scroll', syncDock, { passive: true });
    addEventListener('resize', syncDock);
  }


  /* ── "Кой размер?" jumps to the ladder rather than opening a modal: one less
        layer to dismiss, and the answer stays linkable. */
  const guide = $('[data-open-sizes]');
  if (guide) guide.addEventListener('click', () => $('#sizes')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));

  /* ── Add-to-cart feedback. Three states in one width so the button never
        jumps: rest → done → rest. */
  $$('[data-atc]').forEach(b => b.addEventListener('click', () => {
    if (b.dataset.busy) return;
    const label = b.textContent; b.dataset.busy = '1';
    b.textContent = 'Добавено ✓';
    const n = $('.cart-n'); if (n) n.textContent = String((parseInt(n.textContent, 10) || 0) + 1);
    setTimeout(() => { b.textContent = label; delete b.dataset.busy; }, 1600);
  }));

  /* ── Quiz: one question per screen, progress bar tracks position. */
  const steps = $$('.qz-step');
  if (steps.length) {
    const bars = $$('.qz-bar i'), now = $('[data-q-now]'), back = $('[data-q-back]');
    const groups = $$('.qz-res');
    let at = 0, pick = '';
    /* Резултатът е по вид: отговорът на първия въпрос носи data-col с handle
       на колекция. Вид без колекция („Птица", „Рибка") няма атрибут и пада
       на резервната група — по-честно от това да му покажем играчки за куче. */
    const result = () => {
      if (!groups.length) return;
      let hit = groups.find(g => g.dataset.result === pick);
      if (!hit) hit = groups.find(g => g.dataset.result === '');
      groups.forEach(g => g.classList.toggle('is-on', g === hit));
    };
    const show = i => {
      at = Math.max(0, Math.min(steps.length - 1, i));
      steps.forEach((s, k) => s.classList.toggle('is-on', k === at));
      bars.forEach((b, k) => b.classList.toggle('is-on', k <= at));
      if (now) now.textContent = String(Math.min(at + 1, bars.length));
      if (back) back.disabled = at === 0;
      if (at === steps.length - 1) result();
      $('#qz')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    $$('[data-pick]').forEach(opt => opt.addEventListener('click', () => {
      $$('[data-pick]', opt.closest('.qz-step')).forEach(o => o.classList.remove('is-on'));
      opt.classList.add('is-on');
      /* Видът се взема от ВСЕКИ отговор на въпроса, който изобщо носи
         handle-ове — иначе „Назад“ и смяна на Куче с Птица оставяше
         стария избор и резултатът показваше играчки за куче. */
      const owner = opt.closest('.qz-step');
      if (owner && owner.querySelector('[data-col]')) pick = opt.dataset.col || '';
      setTimeout(() => show(at + 1), 220);
    }));
    back?.addEventListener('click', () => show(at - 1));
    show(0);
  }
})();

/* ── Video: only the hero autoplays. Everything else waits until it is on
      screen, so a page load never pulls three clips at once. Reduced motion
      stops the download entirely rather than just pausing playback. */
(() => {
  'use strict';
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('video.vid').forEach(v => { v.removeAttribute('autoplay'); v.pause(); v.remove(); });
    return;
  }
  const lazy = [...document.querySelectorAll('video[data-inview]')];
  if (!lazy.length || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(entries => entries.forEach(e => {
    if (!e.isIntersecting) { e.target.pause(); return; }
    if (e.target.preload === 'none') e.target.preload = 'auto';
    e.target.play().catch(() => {});
  }), { threshold: 0.25 });
  lazy.forEach(v => io.observe(v));
})();

/* ── Horizontal rails. Arrows scroll by one card; disabled state reflects the
      real scroll position so the buttons never lie about what is left. */
(() => {
  'use strict';
  document.querySelectorAll('[data-rail]').forEach(rail => {
    const head = rail.previousElementSibling;
    const prev = head && head.querySelector('[data-rail-prev]');
    const next = head && head.querySelector('[data-rail-next]');
    if (!prev || !next) return;
    const step = () => {
      const first = rail.firstElementChild;
      if (!first) return rail.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 24;
      return first.getBoundingClientRect().width + gap;
    };
    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max;
    };
    prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left:  step(), behavior: 'smooth' }));
    rail.addEventListener('scroll', sync, { passive: true });
    addEventListener('resize', sync);
    sync();
  });
})();


/* ── Header wash. Standalone block: the earlier version sat inside a larger
      IIFE and never bound. Transparent at rest, light warm grey once moving. */
(() => {
  'use strict';
  const hdr = document.querySelector('.hdr');
  window.__zzWash = hdr ? 'bound' : 'no-header';
  if (!hdr) return;
  /* Ref ~/maxilin/site: секцията сама се обявява за тъмна чрез клас, вместо
     скриптът да пази списък със селектори, който изтича при всяка нова секция. */
  document.querySelectorAll('.quiz-cta, .tick, .ft, .ft__reveal')
          .forEach(s => s.classList.add('is-dark'));
  const darks = [...document.querySelectorAll('.is-dark')];
  const wash = () => {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    hdr.classList.toggle('is-grey', y > 16);
    /* сондата е точно под долния ръб на хедъра — това е пикселът, върху който
       текстът му реално стои */
    const probe = hdr.getBoundingClientRect().bottom - 8;
    const over = darks.some(s => {
      const r = s.getBoundingClientRect();
      return r.top <= probe && r.bottom >= probe;
    });
    hdr.classList.toggle('on-dark', over);
    window.__zzY = y; window.__zzDark = over;
  };
  wash();
  window.addEventListener('scroll', wash, { passive: true });
  window.addEventListener('resize', wash);
})();


/* ── Мобилно меню. Панелът е извън <header>, за да не наследи неговия
      stacking context; хамбургерът остава над него и се превръща в X. */
(() => {
  'use strict';
  const burger = document.getElementById('burger');
  const mnav   = document.getElementById('mnav');
  if (!burger || !mnav) return;
  const set = open => {
    /* Панелът тръгва от реалния долен ръб на хедъра. При скрол 0 над него
       стои и лентата с обявата и фиксираният `top:var(--header-h)` го
       вдигаше 38px нагоре — първата връзка влизаше под хедъра. */
    if (open) {
      const hdr = document.querySelector('.hdr');
      if (hdr) mnav.style.top = Math.max(0, hdr.getBoundingClientRect().bottom) + 'px';
    }
    mnav.hidden = !open;
    burger.classList.toggle('is-open', open);
    burger.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('nav-open', open);
  };
  burger.addEventListener('click', () => set(mnav.hidden));
  /* клик върху връзка затваря — иначе панелът остава отворен върху целта */
  mnav.addEventListener('click', e => { if (e.target.closest('a')) set(false); });
  addEventListener('keydown', e => { if (e.key === 'Escape' && !mnav.hidden) set(false); });
  /* при завъртане на телефона в широк изглед менюто няма смисъл */
  const wide = matchMedia('(min-width:761px)');
  (wide.addEventListener ? wide.addEventListener('change', e => e.matches && set(false))
                         : wide.addListener(e => e.matches && set(false)));
})();
